import type { JobDetails } from "services/jobs";

const INTEGER_OR_DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * Formats a whole number with "." thousand separators (de-DE), e.g.
 * "120000" -> "120.000". Salaries come from free-text vacancy fields, so
 * anything that is not a plain number ("Competitive", "50k", "120,000") is
 * returned unchanged, and a missing value becomes "".
 */
export function formatNumberWithSeparator(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined) return "";

  if (typeof value === "number") {
    return Number.isFinite(value)
      ? new Intl.NumberFormat("de-DE").format(Math.trunc(value))
      : "";
  }

  const trimmed = String(value).trim();
  if (!INTEGER_OR_DECIMAL.test(trimmed)) return String(value);

  return new Intl.NumberFormat("de-DE").format(parseInt(trimmed, 10));
}

/** "1 open role", "3 open roles". */
export function pluralize(count: number, singular: string, plural?: string) {
  return `${count} ${count === 1 ? singular : plural ?? `${singular}s`}`;
}

/** "50.000 – 70.000 USD per year", or null when no salary is given. */
export function formatSalary(job: JobDetails): string | null {
  const min = formatNumberWithSeparator(job.minimumSalary);
  const max = formatNumberWithSeparator(job.maximumSalary);
  const range = min && max && min !== max ? `${min} – ${max}` : min || max;
  if (!range) return job.salaryDescription?.trim() || null;
  return [range, job.currency, job.salaryTime?.toLowerCase()]
    .filter(Boolean)
    .join(" ");
}
